/**
 * Le stringhe di PrimeVue, in italiano.
 *
 * Non è cosmesi: senza questo file il pannello mostra `No results found`, `Choose
 * Date`, `Starts with` e `Yes / No` — cioè l'interfaccia parla inglese proprio
 * nei punti in cui l'utente si è fermato perché qualcosa non tornava. È il
 * divieto 9 del sistema di progetto, e la libreria lo violerebbe per conto
 * nostro.
 *
 * Alcune scelte meritano una riga di spiegazione, perché la traduzione letterale
 * sarebbe peggio dell'inglese:
 *
 *  - `emptyMessage` / `emptyFilterMessage`: «Nessun risultato» da solo non è uno
 *    stato vuoto accettabile (§8.6). Queste due stringhe sono la rete di
 *    sicurezza per i componenti che non hanno un modello vuoto proprio; dove
 *    conta, il vuoto lo disegna `EmptyState` e dice anche cosa fare.
 *  - `dateFormat`: `dd/mm/yy` nella grammatica di PrimeVue significa
 *    giorno/mese/**anno a quattro cifre**. `yy` non è un anno a due cifre — è la
 *    trappola classica di questa libreria, e scriverlo `yyyy` produce `02026`.
 *  - `firstDayOfWeek: 1`: la settimana italiana comincia di lunedì. Con lo zero
 *    predefinito, ogni calendario del pannello sarebbe sfalsato di un giorno
 *    rispetto a qualunque calendario appeso in azienda.
 *  - I mesi e i giorni sono in minuscolo, come vuole l'italiano. `Gennaio` con
 *    la maiuscola è un calco dall'inglese.
 */

export const it = {
  startsWith: 'Inizia con',
  contains: 'Contiene',
  notContains: 'Non contiene',
  endsWith: 'Finisce con',
  equals: 'Uguale a',
  notEquals: 'Diverso da',
  noFilter: 'Nessun filtro',
  lt: 'Minore di',
  lte: 'Minore o uguale a',
  gt: 'Maggiore di',
  gte: 'Maggiore o uguale a',
  dateIs: 'Data uguale a',
  dateIsNot: 'Data diversa da',
  dateBefore: 'Data precedente a',
  dateAfter: 'Data successiva a',
  clear: 'Azzera',
  apply: 'Applica',
  matchAll: 'Tutte le condizioni',
  matchAny: 'Almeno una condizione',
  addRule: 'Aggiungi condizione',
  removeRule: 'Togli condizione',

  accept: 'Sì',
  reject: 'No',
  choose: 'Scegli',
  upload: 'Carica',
  cancel: 'Annulla',
  completed: 'Completato',
  pending: 'In attesa',
  fileSizeTypes: ['B', 'kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],

  dayNames: ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'],
  dayNamesShort: ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'],
  dayNamesMin: ['D', 'L', 'M', 'M', 'G', 'V', 'S'],
  monthNames: [
    'gennaio',
    'febbraio',
    'marzo',
    'aprile',
    'maggio',
    'giugno',
    'luglio',
    'agosto',
    'settembre',
    'ottobre',
    'novembre',
    'dicembre',
  ],
  monthNamesShort: [
    'gen',
    'feb',
    'mar',
    'apr',
    'mag',
    'giu',
    'lug',
    'ago',
    'set',
    'ott',
    'nov',
    'dic',
  ],
  chooseYear: 'Scegli l’anno',
  chooseMonth: 'Scegli il mese',
  chooseDate: 'Scegli la data',
  prevDecade: 'Decennio precedente',
  nextDecade: 'Decennio successivo',
  prevYear: 'Anno precedente',
  nextYear: 'Anno successivo',
  prevMonth: 'Mese precedente',
  nextMonth: 'Mese successivo',
  prevHour: 'Ora precedente',
  nextHour: 'Ora successiva',
  prevMinute: 'Minuto precedente',
  nextMinute: 'Minuto successivo',
  prevSecond: 'Secondo precedente',
  nextSecond: 'Secondo successivo',
  am: 'am',
  pm: 'pm',
  today: 'Oggi',
  weekHeader: 'Sett',
  firstDayOfWeek: 1,
  showMonthAfterYear: false,
  dateFormat: 'dd/mm/yy',
  weak: 'Debole',
  medium: 'Media',
  strong: 'Forte',
  passwordPrompt: 'Scegli una password',

  emptyFilterMessage: 'Nessun risultato con questi filtri',
  searchMessage: '{0} risultati disponibili',
  selectionMessage: '{0} elementi selezionati',
  emptySelectionMessage: 'Nessun elemento selezionato',
  emptySearchMessage: 'Nessun risultato',
  fileChosenMessage: '{0} file',
  noFileChosenMessage: 'Nessun file scelto',
  emptyMessage: 'Nessun risultato',

  aria: {
    trueLabel: 'Vero',
    falseLabel: 'Falso',
    nullLabel: 'Non impostato',
    star: '1 stella',
    stars: '{star} stelle',
    selectAll: 'Seleziona tutto',
    unselectAll: 'Deseleziona tutto',
    close: 'Chiudi',
    previous: 'Precedente',
    next: 'Successivo',
    navigation: 'Navigazione',
    scrollTop: 'Torna in cima',
    moveTop: 'Sposta in cima',
    moveUp: 'Sposta su',
    moveDown: 'Sposta giù',
    moveBottom: 'Sposta in fondo',
    moveToTarget: 'Sposta a destra',
    moveToSource: 'Sposta a sinistra',
    moveAllToTarget: 'Sposta tutto a destra',
    moveAllToSource: 'Sposta tutto a sinistra',
    pageLabel: 'Pagina {page}',
    firstPageLabel: 'Prima pagina',
    lastPageLabel: 'Ultima pagina',
    nextPageLabel: 'Pagina successiva',
    prevPageLabel: 'Pagina precedente',
    rowsPerPageLabel: 'Righe per pagina',
    previousPageLabel: 'Pagina precedente',
    jumpToPageDropdownLabel: 'Vai alla pagina',
    jumpToPageInputLabel: 'Vai alla pagina',
    selectRow: 'Seleziona la riga',
    unselectRow: 'Deseleziona la riga',
    expandRow: 'Espandi la riga',
    collapseRow: 'Comprimi la riga',
    showFilterMenu: 'Mostra il menu dei filtri',
    hideFilterMenu: 'Nascondi il menu dei filtri',
    filterOperator: 'Operatore del filtro',
    filterConstraint: 'Condizione del filtro',
    editRow: 'Modifica la riga',
    saveEdit: 'Salva',
    cancelEdit: 'Annulla',
    listView: 'Vista a elenco',
    gridView: 'Vista a griglia',
    slide: 'Diapositiva',
    slideNumber: 'Diapositiva {slideNumber}',
    zoomImage: 'Ingrandisci',
    zoomIn: 'Ingrandisci',
    zoomOut: 'Riduci',
    rotateRight: 'Ruota a destra',
    rotateLeft: 'Ruota a sinistra',
    listLabel: 'Elenco di opzioni',
    selectColor: 'Scegli il colore',
    removeLabel: 'Togli',
    browseFiles: 'Sfoglia i file',
    maximizeLabel: 'Ingrandisci a tutto schermo',
  },
};
